import { AssetLineConfig, CONFIGURATION } from "../elements/TextAssetLines";
import {
  AssetLineDraftStateT,
  AssetLineKindT,
  AssetLineT,
  useNotDeletedImagesAssetLinesQuery,
  useNotDeletedTextsAssetLinesQuery,
  usePublishDraftAssetLinesMutation,
} from "../graphql/generated/graphql";

type TextKind =
  | AssetLineKindT.ShortHeadlineT
  | AssetLineKindT.LongHeadlineT
  | AssetLineKindT.ShortDescriptionT
  | AssetLineKindT.LongDescriptionT;

type AssetLineMeta = Pick<AssetLineT, "kind" | "draftState">;

const validateImageAssetsChanges = (imageAssetLines: AssetLineMeta[]): boolean => {
  const IMAGE_ASSETS_TO_VALIDATE: AssetLineKindT[] = [
    AssetLineKindT.MarketingImageT,
    AssetLineKindT.SquareMarketingImageT,
  ];
  return IMAGE_ASSETS_TO_VALIDATE.every((kindToValidate): boolean => {
    const existingAssets = imageAssetLines.filter((asset) => asset.kind === kindToValidate);
    const assetsToDelete = existingAssets.filter((asset) => asset.draftState === AssetLineDraftStateT.DeleteT);
    return existingAssets.length - assetsToDelete.length >= 1;
  });
};

const validateTextAssetsChanges = (textAssetLines: AssetLineT[], limits: AssetLineConfig) => {
  const TEXT_ASSETS_TO_VALIDATE: TextKind[] = [
    AssetLineKindT.LongHeadlineT,
    AssetLineKindT.ShortHeadlineT,
    AssetLineKindT.ShortDescriptionT,
    AssetLineKindT.LongDescriptionT,
  ];

  return TEXT_ASSETS_TO_VALIDATE.every((kindToValidate) => {
    const existingAssets = textAssetLines.filter((asset) => asset.kind === kindToValidate);
    const assetsToDelete = existingAssets.filter((asset) => asset.draftState === AssetLineDraftStateT.DeleteT);
    return existingAssets.length - assetsToDelete.length >= (limits[kindToValidate]?.minLimit ?? 0);
  });
};

export const useAssetPublish = () => {
  const { data: dataNotDeletedImageAssetLines } = useNotDeletedImagesAssetLinesQuery();
  const { data: dataNotDeletedTextAssetLines } = useNotDeletedTextsAssetLinesQuery();

  const [publish, { loading: loadingPublish }] = usePublishDraftAssetLinesMutation({
    variables: {
      kinds: [
        AssetLineKindT.MarketingImageT,
        AssetLineKindT.SquareMarketingImageT,
        AssetLineKindT.LongHeadlineT,
        AssetLineKindT.ShortHeadlineT,
        AssetLineKindT.ShortDescriptionT,
        AssetLineKindT.LongDescriptionT,
      ],
    },
  });

  const hasAnyDrafts = !!(
    (dataNotDeletedImageAssetLines?.organization?.notDeletedImagesAssetLines || []).find((line) => line.draftState) ||
    (dataNotDeletedTextAssetLines?.organization?.notDeletedTextsAssetLines || []).find((line) => line.draftState)
  );

  const areTextAssetsPendingChangesValid = validateTextAssetsChanges(
    dataNotDeletedTextAssetLines?.organization?.notDeletedTextsAssetLines || [],
    CONFIGURATION
  );

  const areImageAssetsPendingChangesValid = validateImageAssetsChanges(
    dataNotDeletedImageAssetLines?.organization?.notDeletedImagesAssetLines || []
  );

  const canPublish = areTextAssetsPendingChangesValid && areImageAssetsPendingChangesValid;

  return {
    hasAnyDrafts,
    canPublish,
    publish,
    loadingPublish,
  };
};
